import React, { useState } from 'react';
import { 
  FileSignature, Plus, Search, Eye, Download, Trash2, Loader2
} from 'lucide-react';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import api from '../api';
import CreateAgreementModal from './CreateAgreementModal';
import AgreementDetailsModal from './AgreementDetailsModal';

export default function Agreements() {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  React.useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/employee-services');
      setAgreements(res.data || []);
    } catch (err) {
      console.error("Error fetching agreements:", err);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/employee-services/${id}`);
        setAgreements(agreements.filter(a => a.id !== id));
        Swal.fire('Deleted!', 'Agreement has been deleted.', 'success');
      } catch (err) {
        Swal.fire('Error!', 'Failed to delete agreement.', 'error');
      }
    }
  };

  const filtered = agreements.filter(a =>
    a.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.center_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Main Agreement Table Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        
        {/* Card Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between gap-4">
          <h1 className="text-xl font-black text-slate-800 tracking-tight">Employee Agreements</h1>

          <div className="relative w-full max-w-[300px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text"
              placeholder="Search employee or center..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-[13px] font-medium text-slate-700 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors shadow-sm"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-white border-b border-gray-100 text-[10px] uppercase font-black text-slate-400 tracking-widest whitespace-nowrap">
                <th className="px-6 py-4">EMPLOYEE NAME</th>
                <th className="px-4 py-4">CENTER NAME</th>
                <th className="px-4 py-4">DATE SIGNED</th>
                <th className="px-6 py-4 text-right">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50/60 transition-colors group">
                  
                  {/* EMPLOYEE NAME */}
                  <td className="px-6 py-2.5">
                    <span className="text-[13px] font-bold text-slate-800">{row.employee_name}</span>
                  </td>

                  {/* CENTER NAME */}
                  <td className="px-4 py-2.5">
                    <span className={`text-[13px] font-bold ${row.center_name === 'PANAPUR BSDC' ? 'text-indigo-500' : 'text-slate-600'}`}>
                      {row.center_name}
                    </span>
                  </td>

                  {/* DATE SIGNED */}
                  <td className="px-4 py-2.5">
                    <span className="text-[13px] font-medium text-slate-600">
                      {new Date(row.agreed_at || row.created_at).toLocaleString()}
                    </span>
                  </td>

                  {/* ACTIONS */}
                  <td className="px-6 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      {/* View Details */}
                      <button 
                        onClick={() => setSelectedAgreement(row)}
                        className="w-8 h-8 rounded-full border border-indigo-100 bg-indigo-50 text-indigo-500 hover:bg-indigo-500 hover:text-white flex items-center justify-center transition-colors shadow-sm"
                        title="View Details"
                      >
                        <Eye size={14} strokeWidth={2.5} />
                      </button>
                      
                      {/* Delete */}
                      <button 
                        onClick={() => handleDelete(row.id)}
                        className="w-8 h-8 rounded-full border border-red-100 bg-red-50 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors shadow-sm"
                        title="Delete"
                      >
                        <Trash2 size={14} strokeWidth={2.5} />
                      </button>
                    </div>
                  </td>
                  
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination placeholder */}
        <div className="px-6 py-4 bg-white border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs font-bold text-slate-500">
            Showing <span className="text-slate-800">1</span> to <span className="text-slate-800">{filtered.length}</span> of <span className="text-slate-800">{agreements.length}</span> entries
          </div>
        </div>

      </div>

      {/* Modals */}
      <AgreementDetailsModal 
        isOpen={!!selectedAgreement}
        onClose={() => setSelectedAgreement(null)} 
        agreement={selectedAgreement}
      />
    </div>
  );
}
